import React, { useEffect, useContext, useRef } from 'react';
import { useLocation } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { IonList, IonText } from '@ionic/react';
import useProfile from '../../global/useProfile';
import { AuthContext } from '../authentication/AuthContext';
import { LiveFightsLoader } from '../skeletalLoaders/SkeletalLoaders';
import '../../styles/profile/Profile.scss';
import LiveFightCard from './LiveFightCard';

/**
 * Component for displaying a user's live fights in the profile page.
 * @param {Object} props
 * @param {Object[]} props.liveFights - Array of live fight objects.
 * @returns
 */
function LiveFights({ liveFights, routeName, refreshing = false, onAcceptFight }) {
  const [reloadProfile, profile] = useProfile();
  const authCtx = useContext(AuthContext);
  const location = useLocation();
  const uniqueKeys = useRef([...Array(2)].map(() => uuidv4()));
  const uniqueKeys1 = useRef([...Array(2)].map(() => uuidv4()));

  const formatName = (fullName, profileName) => {
    if (fullName === profileName) {
      return fullName;
    }
    const names = fullName?.trim()?.split(' ');
    if (names?.length > 1) {
      return `${names[0]} ${names[names.length - 1][0]}.`;
    }
    return fullName;
  };

  useEffect(() => {
    if (
      !(
        location.pathname === routeName ||
        location.pathname === `${routeName}/` ||
        location.pathname === routeName.substring(0, routeName.length - 1)
      )
    ) {
      return;
    }
    reloadProfile();
  }, [authCtx.tokens, location, reloadProfile, routeName]);

  const pendingFights = liveFights.filter(fight => fight.accepted === 2);
  const onTableFights = liveFights.filter(fight => fight.accepted === 1);

  let pending;

  if (refreshing) {
    pending = (
      <div>
        {Array.from({ length: 2 }).map((_, index) => (
          <LiveFightsLoader key={uniqueKeys.current[index]} />
        ))}
      </div>
    );
  } else if (pendingFights && pendingFights.length > 0) {
    pending = pendingFights.map(fight => (
      <LiveFightCard
        fight={{
          bet: fight,
          sport: fight?.sport,
          makerName: fight?.makerName === profile?.name ? fight?.makerName : formatName(fight?.makerName),
          makerTeam: fight.makerOutcomeTeam,
          makerOutcomePhrase: fight?.makerOutcomePhrase,
          makerTeamColor: fight.makerOutcomeTeamColor,
          takerName: fight?.takerName === profile?.name ? fight?.takerName : formatName(fight?.takerName),
          takerTeam: fight.takerOutcomeTeam,
          takerTeamColor: fight.takerOutcomeTeamColor,
          takerOutcomePhrase: fight?.takerOutcomePhrase,
          restaurantId: fight.restaurantId,
          msg:
            profile?.name === fight?.makerName
              ? `${fight.makerOutcomePhrase} for ${fight.restaurantItems
                  .map(restItem => `x${restItem.quantity} ${restItem.name}`)
                  .join(', ')} at ${fight.restaurantName}`
              : `${fight.takerOutcomePhrase} for ${fight.restaurantItems
                  .map(restItem => `x${restItem.quantity} ${restItem.name}`)
                  .join(', ')} at ${fight.restaurantName}`,
          time: new Date(fight.timestamp * 1000).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
          }),
          pending: true,
          fightId: fight.id,
          // address: fight.restaurantAddress,
        }}
        key={fight.id}
        onFightAction={reloadProfile}
        profileName={profile?.name}
        isAccepted={false}
        onAcceptFight={onAcceptFight}
      />
    ));
  } else {
    pending = <IonText className='no-fights-text'>None... Start some FoodFights</IonText>;
  }

  let inplay;

  if (refreshing) {
    inplay = (
      <div>
        {Array.from({ length: 2 }).map((_, index) => (
          <LiveFightsLoader key={uniqueKeys1.current[index]} />
        ))}
      </div>
    );
  } else if (onTableFights && onTableFights.length > 0) {
    inplay = onTableFights.map(fight => (
      <LiveFightCard
        fight={{
          bet: fight,
          sport: fight?.sport,
          makerName: fight?.makerName === profile?.name ? fight?.makerName : formatName(fight?.makerName),
          makerTeam: fight.makerOutcomeTeam,
          makerOutcomePhrase: fight?.makerOutcomePhrase,
          makerTeamColor: fight.makerOutcomeTeamColor,
          takerName: fight?.takerName === profile?.name ? fight?.takerName : formatName(fight?.takerName),
          takerTeam: fight.takerOutcomeTeam,
          takerOutcomePhrase: fight?.takerOutcomePhrase,
          takerTeamColor: fight.takerOutcomeTeamColor,
          msg:
            profile?.name === fight?.makerName
              ? `${fight.makerOutcomePhrase} for ${fight.restaurantItems
                  .map(restItem => `x${restItem.quantity} ${restItem.name}`)
                  .join(', ')} at ${fight.restaurantName}`
              : `${fight.takerOutcomePhrase} for ${fight.restaurantItems
                  .map(restItem => `x${restItem.quantity} ${restItem.name}`)
                  .join(', ')} at ${fight.restaurantName}`,
          time: new Date(fight.timestamp * 1000).toLocaleString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            second: 'numeric',
          }),
          pending: false,
          fightId: fight.id,
        }}
        key={fight.id}
        onFightAction={reloadProfile}
        profileName={profile?.name}
        isAccepted
      />
    ));
  } else {
    inplay = <IonText className='no-fights-text'>None... Start some FoodFights</IonText>;
  }

  return (
    <IonList className='live-list'>
      <div className='live-list-section'>
        <p className='live-component-title'>PENDING</p>
        {pending}
      </div>
      <div className='live-list-section'>
        <p className='live-component-title'>IN-PLAY</p>
        {inplay}
      </div>
    </IonList>
  );
}

export default LiveFights;
